/**
 * 📖 Creating a new store? Read: docs/data-provider-guide.md
 *    - Use getDataProvider() for all data operations
 *    - Never import supabase directly
 */
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { getDataProvider, subscribeToTable, type Subscription, type ChangePayload } from '@/lib/dataProvider'
import { useAuthStore } from './auth'
import { hueBackgroundService } from '@/services/hue/HueBackgroundService'
import { wledBackgroundService } from '@/services/wled/WledBackgroundService'
import { logger } from '@/lib/logger'

// TypeScript interfaces based on the database schema
export interface Scene {
  id: string
  user_id: string
  name: string
  description: string | null
  parent_id: string | null
  hierarchy_level: number
  is_active: boolean
  sort_order: number
  created_at: string
  updated_at: string
  graph_position_x: number | null
  graph_position_y: number | null
  container_id: string | null
}

export interface CreateSceneData {
  name: string
  description?: string
  parent_id?: string | null
  graph_position_x?: number | null
  graph_position_y?: number | null
}

export const useScenesStore = defineStore('scenes', () => {
  // State
  const scenes = ref<Map<string, Scene>>(new Map())
  const breadcrumbPath = ref<Scene[]>([])
  const loadingStates = ref<Map<string, boolean>>(new Map())
  const error = ref<string | null>(null)
  // Version counter to force reactivity updates when scenes are updated
  const scenesVersion = ref(0)
  // Cached active scene ID for O(1) lookup instead of iterating all scenes
  const activeSceneId = ref<string | null>(null)
  // Internal subscription reference - managed by the store
  let realtimeSubscription: Subscription | null = null
  // Deduplication for fetch
  let fetchPromise: Promise<{ success: boolean; error?: string }> | null = null

  // Getters
  const getActiveScene = computed(() => {
    // Reference scenesVersion to ensure reactivity when scenes are updated
    const _version = scenesVersion.value
    // O(1) lookup using cached activeSceneId
    if (activeSceneId.value) {
      return scenes.value.get(activeSceneId.value) || null
    }
    return null
  })

  const getSiblings = computed(() => (parentId: string | null) => {
    return Array.from(scenes.value.values())
      .filter(scene => scene.parent_id === parentId)
      .sort((a, b) => a.sort_order - b.sort_order)
  })

  const getChildren = computed(() => (sceneId: string) => {
    return Array.from(scenes.value.values())
      .filter(scene => scene.parent_id === sceneId)
      .sort((a, b) => a.sort_order - b.sort_order)
  })

  const isLoading = computed(() => (key: string) => {
    return loadingStates.value.get(key) || false
  })

  // Helper functions
  const setLoading = (key: string, loading: boolean) => {
    loadingStates.value.set(key, loading)
  }

  const setError = (errorMessage: string | null) => {
    error.value = errorMessage
  }

  const addScene = (scene: Scene) => {
    const existingScene = scenes.value.get(scene.id)
    const wasActive = existingScene?.is_active
    const isNowActive = scene.is_active
    
    scenes.value.set(scene.id, scene)
    // Increment version to trigger reactivity for computed properties
    scenesVersion.value++
    
    // Update cached active scene ID
    if (isNowActive) {
      activeSceneId.value = scene.id
    } else if (wasActive && !isNowActive && activeSceneId.value === scene.id) {
      // This scene was active but no longer is
      activeSceneId.value = null
    }
    
    // Debug logging for multi-tab sync
    if (wasActive !== isNowActive) {
      console.log('[Scenes Store] Scene active state changed:', {
        sceneId: scene.id,
        sceneName: scene.name,
        wasActive,
        isNowActive,
        newVersion: scenesVersion.value
      })
    }
  }

  const removeScene = (sceneId: string) => {
    scenes.value.delete(sceneId)
    // Increment version to trigger reactivity for computed properties
    scenesVersion.value++
  }

  // Actions
  const fetchUserScenes = async () => {
    // Deduplicate concurrent calls
    if (fetchPromise) {
      logger.debug('📁 [scenes] Using existing fetch promise')
      return fetchPromise
    }

    const authStore = useAuthStore()
    if (!authStore.user) {
      setError('User not authenticated')
      return { success: false, error: 'User not authenticated' }
    }

    setLoading('fetchScenes', true)
    setError(null)

    fetchPromise = (async () => {
      try {
        const provider = getDataProvider()
        const data = await provider.query<Scene>('scenes', {
          orderBy: [{ column: 'name', ascending: true }]
        })

        // Update scenes map
        scenes.value.clear()
        data.forEach(scene => {
          scenes.value.set(scene.id, scene)
        })

        // Find and cache active scene ID for O(1) lookup
        const activeScene = data.find(scene => scene.is_active)
        activeSceneId.value = activeScene?.id || null
        
        if (activeScene) {
          try {
            await hueBackgroundService.setActiveScene(activeScene.id)
            await wledBackgroundService.setActiveScene(activeScene.id)
            logger.info(`🎬 Notified background services of active scene on load: ${activeScene.id}`)
          } catch (error) {
            logger.warn('Failed to notify background services of active scene:', error)
          }
        }

        // Initialize realtime subscription after successful fetch
        initializeRealtimeSubscription()

        logger.info('📁 Loaded scenes:', data.length)
        return { success: true }
      } catch (err) {
        error.value = err instanceof Error ? err.message : 'Failed to load scenes'
        return { success: false, error: error.value }
      } finally {
        loadingStates.value.set('loadScenes', false)
        fetchPromise = null
      }
    })()

    return fetchPromise
  }

  const buildBreadcrumbPath = async (sceneId: string) => {
    const path: Scene[] = []
    let currentScene = scenes.value.get(sceneId)

    // Build path by traversing up the hierarchy
    while (currentScene) {
      path.unshift(currentScene)
      if (currentScene.parent_id) {
        currentScene = scenes.value.get(currentScene.parent_id)
      } else {
        break
      }
    }

    // Always extend the path to show children if they exist
    // This ensures we can navigate back down the hierarchy
    const selectedScene = scenes.value.get(sceneId)
    if (selectedScene) {
      const extendedPath = extendPathWithChildren(path, selectedScene.id)
      breadcrumbPath.value = extendedPath
    } else {
      breadcrumbPath.value = path
    }

    return breadcrumbPath.value
  }

  // Helper function to extend path with children to show navigation options
  const extendPathWithChildren = (currentPath: Scene[], fromSceneId: string): Scene[] => {
    const extendedPath = [...currentPath]
    let currentSceneId = fromSceneId

    // Keep extending the path down as long as there are children
    while (true) {
      const children = getChildren.value(currentSceneId)
      if (children.length === 0) break

      // Just take the first child since we're using router-based navigation now
      const nextChild = children[0]

      extendedPath.push(nextChild)
      currentSceneId = nextChild.id
    }

    return extendedPath
  }

  const activateScene = async (sceneId: string) => {
    const authStore = useAuthStore()
    if (!authStore.user) return { success: false, error: 'User not authenticated' }

    setLoading('activateScene', true)
    setError(null)

    try {
      const provider = getDataProvider()
      
      // Deactivate all scenes, then activate the target
      // This replicates what the RPC function does, but works in both modes
      const allScenes = await provider.query<Scene>('scenes')
      for (const scene of allScenes) {
        if (scene.is_active && scene.id !== sceneId) {
          await provider.update('scenes', scene.id, { is_active: false })
        }
      }
      await provider.update('scenes', sceneId, { is_active: true })

      // Update local state to reflect the change
      scenes.value.forEach(scene => {
        scene.is_active = scene.id === sceneId
      })
      
      // Update cached active scene ID
      activeSceneId.value = sceneId

      // Notify background services of scene change
      try {
        await hueBackgroundService.setActiveScene(sceneId)
        await wledBackgroundService.setActiveScene(sceneId)
        logger.info(`🎬 Notified background services of scene activation: ${sceneId}`)
      } catch (error) {
        logger.warn('Failed to notify background services of scene change:', error)
        // Don't fail the scene activation if background service notification fails
      }

      return { success: true }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to activate scene'
      setError(errorMessage)
      
      // On error, refresh from database to ensure consistency
      await fetchUserScenes()
      
      return { success: false, error: errorMessage }
    } finally {
      setLoading('activateScene', false)
    }
  }

  const deactivateAllScenes = async () => {
    const authStore = useAuthStore()
    if (!authStore.user) return { success: false, error: 'User not authenticated' }

    setLoading('deactivateScene', true)
    setError(null)

    try {
      const provider = getDataProvider()
      
      // Deactivate each active scene
      const allScenes = await provider.query<Scene>('scenes')
      for (const scene of allScenes) {
        if (scene.is_active) {
          await provider.update('scenes', scene.id, { is_active: false })
        }
      }

      // Update local state
      scenes.value.forEach(scene => {
        scene.is_active = false
      })
      
      // Clear cached active scene ID
      activeSceneId.value = null

      // Notify background services
      try {
        await hueBackgroundService.setActiveScene(null)
        await wledBackgroundService.setActiveScene(null)
        logger.info('🎬 Deactivated all scenes')
      } catch (error) {
        logger.warn('Failed to notify background services of scene deactivation:', error)
      }

      return { success: true }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to deactivate scenes'
      setError(errorMessage)
      await fetchUserScenes()
      return { success: false, error: errorMessage }
    } finally {
      setLoading('deactivateScene', false)
    }
  }

  const createScene = async (sceneData: CreateSceneData) => {
    const authStore = useAuthStore()
    if (!authStore.user) return { success: false, error: 'User not authenticated' }

    setLoading('createScene', true)
    setError(null)

    try {
      const provider = getDataProvider()
      
      // Calculate hierarchy level and sort order
      let hierarchyLevel = 0
      let sortOrder = 0

      if (sceneData.parent_id) {
        const parent = scenes.value.get(sceneData.parent_id)
        if (parent) {
          hierarchyLevel = parent.hierarchy_level + 1
        }
        
        // Get highest sort order among siblings
        const siblings = getSiblings.value(sceneData.parent_id)
        sortOrder = siblings.length > 0 ? Math.max(...siblings.map(s => s.sort_order)) + 1 : 0
      } else {
        // Root level scene
        const rootScenes = getSiblings.value(null)
        sortOrder = rootScenes.length > 0 ? Math.max(...rootScenes.map(s => s.sort_order)) + 1 : 0
      }

      // First deactivate all existing scenes
      const allScenes = await provider.query<Scene>('scenes')
      for (const scene of allScenes) {
        if (scene.is_active) {
          await provider.update('scenes', scene.id, { is_active: false })
        }
      }

      // Create the new scene
      const result = await provider.insert<Scene>('scenes', {
        user_id: authStore.user.id,
        name: sceneData.name,
        description: sceneData.description || null,
        parent_id: sceneData.parent_id || null,
        hierarchy_level: hierarchyLevel,
        sort_order: sortOrder,
        is_active: true,
        graph_position_x: sceneData.graph_position_x ?? null,
        graph_position_y: sceneData.graph_position_y ?? null
      })

      if (!result.success || !result.data) {
        throw new Error(result.error || 'No data returned from scene creation')
      }

      // Update local state - deactivate all existing scenes and add new active scene
      scenes.value.forEach(scene => {
        scene.is_active = false
      })
      addScene(result.data)
      
      return { success: true, data: result.data }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create scene'
      setError(errorMessage)
      return { success: false, error: errorMessage }
    } finally {
      setLoading('createScene', false)
    }
  }

  const fetchSiblings = async (parentId: string | null) => {
    setLoading('fetchSiblings', true)
    setError(null)

    try {
      const authStore = useAuthStore()
      if (!authStore.user) throw new Error('User not authenticated')

      const provider = getDataProvider()
      const filters = parentId 
        ? [{ column: 'parent_id', operator: 'eq' as const, value: parentId }]
        : [{ column: 'parent_id', operator: 'eq' as const, value: null }]
      
      const data = await provider.query<Scene>('scenes', {
        filters,
        orderBy: [{ column: 'sort_order', ascending: true }]
      })

      // Store the siblings for the context menu
      return { success: true, data: data || [] }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch siblings'
      setError(errorMessage)
      return { success: false, error: errorMessage }
    } finally {
      setLoading('fetchSiblings', false)
    }
  }

  const deleteScene = async (sceneId: string) => {
    const authStore = useAuthStore()
    if (!authStore.user) return { success: false, error: 'User not authenticated' }

    setLoading('deleteScene', true)
    setError(null)

    try {
      const provider = getDataProvider()
      
      // Get the scene to check if it's active and has a parent
      const scene = scenes.value.get(sceneId)
      if (!scene) {
        throw new Error('Scene not found')
      }

      const isActiveScene = scene.is_active
      const parentId = scene.parent_id

      // Get all descendant scenes before deleting
      const getAllDescendants = (sid: string): string[] => {
        const children = getChildren.value(sid)
        let descendants: string[] = children.map((c: Scene) => c.id)
        
        children.forEach((child: Scene) => {
          const grandChildren = getAllDescendants(child.id)
          descendants = [...descendants, ...grandChildren]
        })
        
        return descendants
      }

      const descendantIds = getAllDescendants(sceneId)
      logger.info(`🗑️ Deleting scene ${sceneId} and ${descendantIds.length} descendants`)

      // Delete the scene from the database (should cascade to children if DB is set up correctly)
      const result = await provider.delete('scenes', sceneId)
      if (!result.success) throw new Error(result.error)

      // Remove from local state - parent and all descendants
      removeScene(sceneId)
      descendantIds.forEach(descendantId => {
        removeScene(descendantId)
      })

      // If we deleted the active scene, activate another one
      if (isActiveScene) {
        // Try to activate a sibling or parent
        const siblings = getSiblings.value(parentId)
        
        if (siblings.length > 0) {
          // Activate the first sibling
          await activateScene(siblings[0].id)
        } else if (parentId) {
          // Activate the parent
          await activateScene(parentId)
        } else {
          // No parent or siblings, try to find any root scene
          const rootScenes = getSiblings.value(null)
          if (rootScenes.length > 0) {
            await activateScene(rootScenes[0].id)
          }
        }
      }

      return { success: true }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete scene'
      setError(errorMessage)
      return { success: false, error: errorMessage }
    } finally {
      setLoading('deleteScene', false)
    }
  }

  // Initialize realtime subscription - call this once when user logs in
  const initializeRealtimeSubscription = () => {
    // Already subscribed? Don't create duplicate
    if (realtimeSubscription) {
      console.log('[Scenes Store] Realtime subscription already active')
      return
    }

    const authStore = useAuthStore()
    if (!authStore.user) {
      console.log('[Scenes Store] Cannot subscribe - no user')
      return
    }

    console.log('[Scenes Store] Setting up realtime subscription for user:', authStore.user.id)

    realtimeSubscription = subscribeToTable<Scene>(
      'scenes',
      (payload: ChangePayload<Scene>) => {
        handleSceneChange(payload)
      },
      { column: 'user_id', value: authStore.user.id }
    )
  }

  // Cleanup subscription - call this on logout
  const cleanupRealtimeSubscription = () => {
    if (realtimeSubscription) {
      console.log('[Scenes Store] Cleaning up realtime subscription')
      realtimeSubscription.unsubscribe()
      realtimeSubscription = null
    }
  }

  // Legacy methods for backward compatibility (deprecated - use initialize/cleanup instead)
  const subscribeToSceneUpdates = () => {
    initializeRealtimeSubscription()
    return realtimeSubscription
  }

  const unsubscribeFromUpdates = (_subscription: Subscription | null) => {
    // No-op for backward compatibility - subscription is now managed internally
    // Components don't need to manage this anymore
  }

  const handleSceneChange = (payload: ChangePayload<Scene>) => {
    const { eventType, new: newRecord, old: oldRecord } = payload

    console.log('[Scenes Store] Realtime event received:', {
      eventType,
      newRecord: newRecord ? { id: newRecord.id, name: newRecord.name, is_active: newRecord.is_active } : null,
      oldRecord: oldRecord ? { id: oldRecord.id, is_active: oldRecord.is_active } : null
    })

    switch (eventType) {
      case 'INSERT':
        if (newRecord) {
          addScene(newRecord)
        }
        break
      
      case 'UPDATE':
        if (newRecord) {
          addScene(newRecord)
        }
        break
      
      case 'DELETE':
        if (oldRecord && oldRecord.id) {
          removeScene(oldRecord.id)
          
          // Remove from breadcrumb path if present
          breadcrumbPath.value = breadcrumbPath.value.filter(scene => scene.id !== oldRecord.id)
        }
        break
    }
  }

  const updateScenePosition = async (sceneId: string, x: number, y: number) => {
    const authStore = useAuthStore()
    
    logger.debug(`🎯 updateScenePosition called for scene ${sceneId} with position (${x}, ${y})`)
    
    if (!authStore.user) {
      logger.warn('❌ User not authenticated, cannot update scene position')
      return { success: false, error: 'User not authenticated' }
    }

    logger.debug(`📝 Updating scene position in database for user ${authStore.user.id}`)

    try {
      const provider = getDataProvider()
      const updateData = { 
        graph_position_x: x,
        graph_position_y: y,
        updated_at: new Date().toISOString()
      }
      
      logger.debug('📤 Sending update with data:', updateData)

      const result = await provider.update<Scene>('scenes', sceneId, updateData)

      logger.debug('📨 Update response:', result)

      if (!result.success) {
        throw new Error(result.error || 'Failed to update')
      }

      logger.info(`✅ Database update successful for scene ${sceneId}`)

      // Update local state
      const scene = scenes.value.get(sceneId)
      if (scene) {
        scene.graph_position_x = x
        scene.graph_position_y = y
        logger.debug(`🔄 Updated local state for scene ${sceneId}`)
      } else {
        logger.warn(`⚠️ Scene ${sceneId} not found in local state`)
      }

      return { success: true }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update scene position'
      logger.error('❌ Error updating scene position:', {
        sceneId,
        position: { x, y },
        error: errorMessage,
        userId: authStore.user?.id
      })
      return { success: false, error: errorMessage }
    }
  }

  const updateSceneContainer = async (sceneId: string, containerId: string | null, position?: { x: number, y: number }) => {
    const authStore = useAuthStore()
    
    if (!authStore.user) {
      return { success: false, error: 'User not authenticated' }
    }

    try {
      const provider = getDataProvider()
      const updateData: any = { 
        container_id: containerId,
        updated_at: new Date().toISOString()
      }
      
      // If position is provided, update it too (for relative positioning)
      if (position) {
        updateData.graph_position_x = position.x
        updateData.graph_position_y = position.y
      }

      const result = await provider.update('scenes', sceneId, updateData)
      if (!result.success) throw new Error(result.error)

      // Update local state
      const scene = scenes.value.get(sceneId)
      if (scene) {
        scene.container_id = containerId
        if (position) {
          scene.graph_position_x = position.x
          scene.graph_position_y = position.y
        }
      }

      logger.info(`📦 Updated scene ${sceneId} container to ${containerId}`)
      return { success: true }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update scene container'
      logger.error('❌ Error updating scene container:', err)
      return { success: false, error: errorMessage }
    }
  }

  const updateSceneName = async (sceneId: string, name: string) => {
    const authStore = useAuthStore()
    
    if (!authStore.user) {
      return { success: false, error: 'User not authenticated' }
    }

    try {
      const provider = getDataProvider()
      const result = await provider.update<Scene>('scenes', sceneId, { 
        name,
        updated_at: new Date().toISOString()
      })

      if (!result.success) throw new Error(result.error)

      // Update local state
      const scene = scenes.value.get(sceneId)
      if (scene && result.data) {
        scene.name = result.data.name
        scene.updated_at = result.data.updated_at
      }

      return { success: true }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update scene name'
      return { success: false, error: errorMessage }
    }
  }

  const updateSceneDescription = async (sceneId: string, description: string) => {
    const authStore = useAuthStore()
    
    if (!authStore.user) {
      return { success: false, error: 'User not authenticated' }
    }

    try {
      const provider = getDataProvider()
      const result = await provider.update<Scene>('scenes', sceneId, { 
        description: description || null,
        updated_at: new Date().toISOString()
      })

      if (!result.success) throw new Error(result.error)

      // Update local state
      const scene = scenes.value.get(sceneId)
      if (scene && result.data) {
        scene.description = result.data.description
        scene.updated_at = result.data.updated_at
      }

      return { success: true }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update scene description'
      return { success: false, error: errorMessage }
    }
  }

  // Parent relationship management helpers
  const getDescendants = (sceneId: string): Scene[] => {
    const children = getChildren.value(sceneId)
    let descendants: Scene[] = [...children]
    
    children.forEach((child: Scene) => {
      const grandChildren = getDescendants(child.id)
      descendants = [...descendants, ...grandChildren]
    })
    
    return descendants
  }

  const getAncestorsArray = (sceneId: string): Scene[] => {
    const ancestors: Scene[] = []
    let currentScene = scenes.value.get(sceneId)
    
    while (currentScene?.parent_id) {
      const parent = scenes.value.get(currentScene.parent_id)
      if (!parent) break
      ancestors.push(parent)
      currentScene = parent
    }
    
    return ancestors
  }

  const detectCycle = (childId: string, newParentId: string | null): boolean => {
    if (!newParentId) return false // No cycle if becoming root
    if (childId === newParentId) return true // Direct self-reference
    
    // Walk up the parent chain from newParentId
    let current: string | null = newParentId
    const visited = new Set<string>()
    
    while (current) {
      if (visited.has(current)) return true // Found a cycle in existing hierarchy
      if (current === childId) return true // Would create cycle
      
      visited.add(current)
      const parent = scenes.value.get(current)
      current = parent?.parent_id || null
    }
    
    return false
  }

  const recalculateHierarchyLevel = async (sceneId: string): Promise<void> => {
    const authStore = useAuthStore()
    if (!authStore.user) throw new Error('User not authenticated')

    const scene = scenes.value.get(sceneId)
    if (!scene) throw new Error('Scene not found')

    const provider = getDataProvider()

    // Calculate new hierarchy level based on parent
    const newLevel = scene.parent_id 
      ? (scenes.value.get(scene.parent_id)?.hierarchy_level ?? 0) + 1 
      : 0

    // Update this scene
    const result = await provider.update('scenes', sceneId, { hierarchy_level: newLevel })
    if (!result.success) throw new Error(result.error)

    // Update local state
    scene.hierarchy_level = newLevel

    // Recursively update all children
    const children = getChildren.value(sceneId)
    for (const child of children) {
      await recalculateHierarchyLevel(child.id)
    }
  }

  const updateSceneParent = async (sceneId: string, newParentId: string | null) => {
    const authStore = useAuthStore()
    if (!authStore.user) return { success: false, error: 'User not authenticated' }

    setLoading('updateSceneParent', true)
    setError(null)

    try {
      const provider = getDataProvider()
      
      // Validate scene exists
      const scene = scenes.value.get(sceneId)
      if (!scene) {
        throw new Error('Scene not found')
      }

      // Validate new parent exists (if provided)
      if (newParentId && !scenes.value.get(newParentId)) {
        throw new Error('Parent scene not found')
      }

      // Check for circular dependencies
      if (detectCycle(sceneId, newParentId)) {
        throw new Error('Cannot create circular parent relationship')
      }

      // Update parent_id in database
      const result = await provider.update('scenes', sceneId, { parent_id: newParentId })
      if (!result.success) throw new Error(result.error)

      // Update local state
      scene.parent_id = newParentId

      // Recalculate hierarchy levels for this scene and all descendants
      await recalculateHierarchyLevel(sceneId)

      logger.info(`✅ Updated scene ${sceneId} parent to ${newParentId || 'root'}`)
      
      return { success: true }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update scene parent'
      setError(errorMessage)
      logger.error('❌ Error updating scene parent:', errorMessage)
      return { success: false, error: errorMessage }
    } finally {
      setLoading('updateSceneParent', false)
    }
  }

  const detachSceneFromParent = async (sceneId: string) => {
    return updateSceneParent(sceneId, null)
  }

  const actions = {
    fetchUserScenes,
    buildBreadcrumbPath,
    activateScene,
    deactivateAllScenes,
    createScene,
    deleteScene,
    fetchSiblings,
    initializeRealtimeSubscription,
    cleanupRealtimeSubscription,
    subscribeToSceneUpdates,
    handleSceneChange,
    unsubscribeFromUpdates,
    updateScenePosition,
    updateSceneContainer,
    updateSceneName,
    updateSceneDescription,
    updateSceneParent,
    detachSceneFromParent,
    getDescendants,
    getAncestorsArray,
    detectCycle
  }

  return {
    // State
    scenes,
    breadcrumbPath,
    loadingStates,
    error,
    
    // Getters
    getActiveScene,
    getSiblings,
    getChildren,
    isLoading,
    
    // Helper functions
    setLoading,
    setError,
    addScene,
    removeScene,
    
    // Actions (to be implemented)
    ...actions
  }
})